'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { resolveTenant, getTenantIdentifier } from '@/utils/tenantResolver';

/**
 * TenantGate ─────────────────────────────────────────────────────────────
 *
 * This component is a CLIENT-SIDE guard that runs on every route to:
 *   1. Detect when a request is landing on an unrecognised custom domain
 *      and redirect it to /domain-not-found.
 *   2. Keep the tenant-resolution cache warm for other client-side code.
 *
 * It is NOT a loading gate any more. In earlier versions this component
 * blocked rendering until tenant resolution completed — which meant the
 * server-rendered HTML was just a <Loader /> spinner, killing SEO for
 * school subdomains. Now it always passes children through and runs its
 * checks in the background via useEffect.
 *
 * Public pages (landing root, auth flow, domain-not-found help) skip
 * the custom-domain redirect entirely since they are meant to be
 * reachable on any host.
 */
export default function TenantGate({ children }) {
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        const isAuthFlow = pathname.startsWith('/auth');
        const isHelpPage = pathname === '/domain-not-found';
        const isLandingRoot = pathname === '/' || pathname === '';

        // Public routes don't gate on tenant resolution. The landing page
        // is server-rendered with tenant detection done on the server, so
        // client-side gating would only slow things down.
        if (isAuthFlow || isHelpPage || isLandingRoot) return;

        let cancelled = false;

        // Safety net: if resolution takes longer than 5s we still let the
        // user through on subdomain/main mode, but redirect unrecognised
        // custom domains to the help page.
        const timeoutId = setTimeout(() => {
            if (cancelled) return;
            const tid = getTenantIdentifier();
            if (tid?.mode === 'customDomain' && pathname !== '/domain-not-found') {
                router.replace('/domain-not-found');
            }
        }, 5000);

        resolveTenant()
            .then((tenant) => {
                if (cancelled) return;
                clearTimeout(timeoutId);
                if (
                    !tenant.resolved &&
                    tenant.mode === 'customDomain' &&
                    pathname !== '/domain-not-found'
                ) {
                    router.replace('/domain-not-found');
                }
            })
            .catch((error) => {
                if (cancelled) return;
                clearTimeout(timeoutId);
                console.error('[TenantGate] Resolution failed:', error);
                const tid = getTenantIdentifier();
                if (tid?.mode === 'customDomain' && pathname !== '/domain-not-found') {
                    router.replace('/domain-not-found');
                }
            });

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [pathname, router]);

    return <>{children}</>;
}
